export const VALID_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/svg+xml",
];

export const VALID_FONT_EXTENSIONS = ["ttf", "otf", "woff", "woff2"];

export const VALID_FONT_MIME_TYPES = [
  "font/ttf",
  "font/otf",
  "font/woff",
  "font/woff2",
  "application/x-font-ttf",
  "application/x-font-otf",
  "application/font-woff",
  "application/font-woff2",
  "application/octet-stream",
];

export const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/x-icon": "ico",
  "image/vnd.microsoft.icon": "ico",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/vnd.adobe.photoshop": "psd",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/x-msvideo": "avi",
  "video/avi": "avi",
  "image/tiff": "tiff",
  "application/pdf": "pdf",
  "application/postscript": "ps",
  "font/ttf": "ttf",
  "font/otf": "otf",
  "font/woff": "woff",
  "font/woff2": "woff2",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/csv": "csv",
  "text/tab-separated-values": "tsv",
  "application/json": "json",
  "text/html": "html",
  "text/xml": "xml",
  "application/xml": "xml",
  "application/yaml": "yaml",
  "text/yaml": "yaml",
};

export const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  tif: "image/tiff",
  tiff: "image/tiff",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  ico: "image/x-icon",
  cur: "image/x-icon",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  avi: "video/x-msvideo",
  ttf: "font/ttf",
  otf: "font/otf",
  woff: "font/woff",
  woff2: "font/woff2",
  pdf: "application/pdf",
  txt: "text/plain",
  log: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  json: "application/json",
  html: "text/html",
  htm: "text/html",
  xml: "text/xml",
  yaml: "application/yaml",
  yml: "application/yaml",
};

export const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
] as const;

/**
 * Both tables above are keyed on values we don't choose — MIME types off the
 * wire and extensions parsed out of user-supplied filenames — so a bare `[]`
 * read returns an inherited `Object.prototype` member for a handful of names
 * (`toString`, `constructor`, …). That value is truthy and is not a string, so
 * it flows on as if it were a real mapping and then throws somewhere further
 * downstream. Look up through these instead of indexing directly.
 */
export const lookupMimeTypeForExtension = (
  extension: string,
): string | undefined =>
  Object.hasOwn(EXTENSION_TO_MIME_TYPE, extension)
    ? EXTENSION_TO_MIME_TYPE[extension]
    : undefined;

export const lookupExtensionForMimeType = (
  mimeType: string,
): string | undefined =>
  Object.hasOwn(MIME_TYPE_TO_EXTENSION, mimeType)
    ? MIME_TYPE_TO_EXTENSION[mimeType]
    : undefined;

/**
 * Prefix check rather than a lookup against {@link VALID_IMAGE_MIME_TYPES}:
 * browsers report image types that list doesn't carry (`image/gif`,
 * `image/avif`, `image/bmp`), and callers that gate image-only work need all of
 * them, not just the ones we accept as uploads.
 */
export const isImageMimeType = (mimeType: string): boolean =>
  mimeType.startsWith("image/");

/**
 * Lowercased extension including the dot (".mp4"), or "" when the name has
 * none. A trailing dot ("archive.") counts as none.
 *
 * Note the two conventions in this file: the lookup tables above are keyed
 * without the dot, this returns it with one.
 */
export const getFileExtension = (fileName: string): string => {
  const index = fileName.lastIndexOf(".");
  if (index < 0 || index === fileName.length - 1) {
    return "";
  }
  return fileName.slice(index).toLowerCase();
};

/**
 * The only extensions we refuse. Attachments are otherwise unrestricted: the
 * backend stores the bytes in S3 and writes them verbatim into the agent's
 * sandboxed filesystem, and the type-specific handling (PDF extraction, image
 * content nodes, font/logo validation) is gated server-side where it's needed.
 * So we block only what is obviously useless or hostile — compiled binaries,
 * installers, disk images — where accepting the file could only waste payload
 * budget or signal a mis-drop.
 *
 * Shared rather than mirrored: the browser check is advisory (a presigned
 * upload can be initiated for any filename), so the server has to enforce the
 * same set, and two copies would drift.
 */
export const BLOCKED_FILE_EXTENSIONS = [
  // Windows executables and launchers
  ".exe",
  ".msi",
  ".msix",
  ".bat",
  ".cmd",
  ".com",
  ".scr",
  ".lnk",
  // Compiled libraries
  ".dll",
  ".so",
  ".dylib",
  // macOS installers and bundles
  ".dmg",
  ".pkg",
  ".app",
  // Mobile and Linux packages
  ".apk",
  ".ipa",
  ".deb",
  ".rpm",
  // Disk images and raw binaries
  ".iso",
  ".bin",
  ".jar",
] as const;

const BLOCKED_EXTENSION_SET = new Set<string>(BLOCKED_FILE_EXTENSIONS);

export const isBlockedFileExtension = (fileName: string): boolean =>
  BLOCKED_EXTENSION_SET.has(getFileExtension(fileName));

export function inferImageMimeType(file: File): string {
  if (file.type && isImageMimeType(file.type)) {
    return file.type;
  }
  // Lookup tables here are keyed without the leading dot.
  const extension = getFileExtension(file.name).slice(1);
  const contentType = extension
    ? lookupMimeTypeForExtension(extension)
    : undefined;
  if (!contentType || !isImageMimeType(contentType)) {
    throw new Error("Unsupported image type");
  }
  return contentType;
}
